import React, { useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { CalendarIcon, XIcon } from 'lucide-react'
import { toastTimeout } from '../../common/constants'
import { Toast } from '../../common/components'
import { getCurrentUserId } from '../../api/auth-service'
import { useBookSeat, useSeatMapBookings, useUserBookings } from '../../common/hooks'
import SeatMapContainer from '../seatmap/SeatMapContainer'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/common/components/ui/dialog'
import { Button } from '@/common/components/ui/button'
import { Calendar } from '@/common/components/ui/calendar'
import { Popover, PopoverTrigger, PopoverContent } from '@/common/components/ui/popover'
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/common/components/ui/select'

const toastInitialState = { show: false, message: '' }

function formatDateRangeLabel(startDate, endDate) {
  if (!startDate) return 'Choose Date Range'
  if (endDate) return `${dayjs(startDate).format('MMM D, YYYY')} – ${dayjs(endDate).format('MMM D, YYYY')}`
  return dayjs(startDate).format('MMM D, YYYY')
}

function GroupBooking({ isOps, show, onClose, data: { offices } }) {
  const [toast, setToast] = useState(toastInitialState)
  const userId = getCurrentUserId()
  const createToastTimeout = () => setTimeout(() => setToast(toastInitialState), toastTimeout)
  const handleError = (error) => setToast({
    show: true,
    message: <>Error: <span className="text-red-600">{error.message}</span>.</>
  })

  const [, requestUserBookings] = useUserBookings(userId)
  const [bookings, requestBookings] = useSeatMapBookings(userId)
  const [, , , , reuqestGroupSeatBookings] = useBookSeat()
  const [officeName, setOfficeName] = useState('Athlone')
  const defaultDate = dayjs().format('YYYY-MM-DD')
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined })
  const { from: startDate, to: endDate } = dateRange
  const [selectedSeats, setSelectedSeats] = useState([])

  const handleChangeOffice = (value) => {
    setOfficeName(value)
    requestBookings(
      startDate == null ? defaultDate : dayjs(startDate).format('YYYY-MM-DD'),
      value
    )
  }

  const handleGroupBookingSeats = (chosenSeats) => {
    setSelectedSeats([...chosenSeats])
  }

  const onSuccessOpr = () => {
    setSelectedSeats([])
    requestBookings()
    setToast({
      show: true,
      message: <>Seat Number '<strong>{selectedSeats.toString()}</strong>' reserved!</>
    })
  }

  const handleGroupBookingClick = () => {
    const startDateFormat = dayjs(startDate).format('YYYY-MM-DD')
    const endDateFormat = dayjs(endDate).format('YYYY-MM-DD')
    reuqestGroupSeatBookings(startDateFormat, endDateFormat, userId, selectedSeats)
      .then(
        onSuccessOpr(),
        handleError
      ).finally(() => {
        requestBookings()
        createToastTimeout()
      })
  }

  const dateRangeLabel = formatDateRangeLabel(startDate, endDate)

  return (
    <>
      <Dialog open={show} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Group Seat Booking</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            <div className="relative w-full">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal pr-8"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 opacity-50" />
                    <span className={startDate ? '' : 'text-muted-foreground'}>
                      {dateRangeLabel}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start">
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={(range) => setDateRange(range ?? { from: undefined, to: undefined })}
                    numberOfMonths={1}
                  />
                </PopoverContent>
              </Popover>
              {startDate && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100"
                  onClick={() => setDateRange({ from: undefined, to: undefined })}
                >
                  <XIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            <Select value={officeName} onValueChange={handleChangeOffice}>
              <SelectTrigger>
                <SelectValue placeholder="Select Office" />
              </SelectTrigger>
              <SelectContent>
                {offices.map((office) => (
                  <SelectItem key={office.id} value={office.name}>
                    {office.location}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="max-h-[400px] overflow-y-auto border border-[#e0e0e0] rounded p-[10px]">
              <SeatMapContainer
                selectedSeats={selectedSeats}
                setSelectedSeats={handleGroupBookingSeats}
                isOps={isOps}
                data={bookings}
                onSuccessOpr={requestUserBookings}
                officeName={officeName}
                isGroupBooking={true}
              />
            </div>

            {selectedSeats.length > 0 && (
              <p className="text-sm bg-gray-100 px-3 py-2 rounded">
                Selected Seats: <strong>{selectedSeats.toString()}</strong>
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleGroupBookingClick}>Reserve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Toast visibility={toast.show}>{toast.message}</Toast>
    </>
  )
}

GroupBooking.propTypes = {
  isOps: PropTypes.bool.isRequired,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.shape({
    offices: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      location: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
}

export default GroupBooking
